import { useForm, useWatch } from "react-hook-form";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { WithAppPreviewLayout } from "@/components/Layouts/WithAppPreviewLayout";
import { OnboardingScreen } from "@/components/OnboardingScreen/OnboardingScreen";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Phone } from "@/components/Phone/Phone";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { getFlagIcon } from "@/helpers/flags";
import { createImageSizeRule, createImageTypeRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { FirstOnboardingScreenState } from "./helper";

const TITLE_MAX_LENGTH = 50;
const SUBTITLE_MAX_LENGTH = 150;

interface CreateOrEditOnboardingScreenFormValues {
  title: string;
  subtitle: string;
  image: FormImage[];
}

export interface OnboardingScreenFormProps {
  isEditMode: boolean;
  defaultValues?: Partial<CreateOrEditOnboardingScreenFormValues>;
  styling: {
    bgColor?: string;
    textColor?: string;
  };
  firstOnboardingScreenState: FirstOnboardingScreenState;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrEditOnboardingScreenFormValues) => void;
}
export function OnboardingScreenForm({
  isEditMode,
  defaultValues,
  styling,
  firstOnboardingScreenState,
  isSubmitting,
  onSubmit,
}: OnboardingScreenFormProps): React.ReactNode {
  const formMethods = useForm<CreateOrEditOnboardingScreenFormValues>({
    defaultValues,
  });

  const { t } = useTranslation();
  const slug = useSlug();

  const title = isEditMode ? t("page.onboarding-screens.edit.title") : t("page.onboarding-screens.new.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.onboarding-screens.title"),
              to: routes.onboardingScreens.list({ slug }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <WithAppPreviewLayout
          preview={<Preview styling={styling} firstOnboardingScreenState={firstOnboardingScreenState} />}
        >
          <Form.Container>
            <CreateEditForm isEditMode={isEditMode} isSubmitting={isSubmitting} />
          </Form.Container>
        </WithAppPreviewLayout>
      </Form.Root>
    </DocumentPage>
  );
}

function CreateEditForm({ isEditMode, isSubmitting }: { isEditMode: boolean; isSubmitting: boolean }) {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();

  const flagIcon = getFlagIcon({
    languageId: sessionUser.language.id,
    width: 16,
  });

  return (
    <section className="flex h-full flex-col justify-between">
      <Form.Body>
        <Notice
          icon={<Translate01Icon />}
          variant="special"
          message={
            isEditMode
              ? t("common.label.you-are-editing-in-original-content-language")
              : t("common.label.your-input-will-be-auto-translated")
          }
        />
        <FormField label={t("page.onboarding-screens.form.title")} actions={flagIcon}>
          <FormInput<CreateOrEditOnboardingScreenFormValues>
            id="title-input"
            name="title"
            rules={{
              maxLength: {
                value: TITLE_MAX_LENGTH,
                message: t("components.form.error.max-length", {
                  length: TITLE_MAX_LENGTH,
                }),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.onboarding-screens.form.subtitle")} actions={flagIcon}>
          <FormTextArea<CreateOrEditOnboardingScreenFormValues>
            width="wide"
            data-testid="subtitle-textarea"
            id="subtitle-input"
            name="subtitle"
            rules={{
              maxLength: {
                value: SUBTITLE_MAX_LENGTH,
                message: t("components.form.error.max-length", {
                  length: SUBTITLE_MAX_LENGTH,
                }),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.onboarding-screens.form.image")} required>
          <FormImageInput<CreateOrEditOnboardingScreenFormValues, "image">
            name="image"
            id="image-input"
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.onboarding-screens.form.image"),
              }),
              validate: {
                size: createImageSizeRule({
                  t,
                }),
                type: createImageTypeRule({
                  t,
                }),
              },
            }}
          />
        </FormField>
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button type="submit" isLoading={isSubmitting}>
            {isEditMode ? t("common.action.save") : t("common.action.create")}
          </Button>
        )}
      />
    </section>
  );
}

function Preview({
  styling,
  firstOnboardingScreenState,
}: {
  styling: OnboardingScreenFormProps["styling"];
  firstOnboardingScreenState: OnboardingScreenFormProps["firstOnboardingScreenState"];
}) {
  const { title, subtitle, image } = useWatch<CreateOrEditOnboardingScreenFormValues>();

  const withFadeOutBackground =
    firstOnboardingScreenState === FirstOnboardingScreenState.HAS_TEXT ||
    (firstOnboardingScreenState === FirstOnboardingScreenState.CURRENT && !!(title || subtitle));

  return (
    <Phone>
      <OnboardingScreen
        imageUrl={image?.[0]?.url || ""}
        title={title}
        subtitle={subtitle}
        styling={styling}
        withFadeOutBackground={withFadeOutBackground}
      />
    </Phone>
  );
}
